import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';
import { db, auth } from './firebase';
import { decompressTranscript } from './utils/decompress';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hideUntitled, setHideUntitled] = useState(true);

  // Check authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (user) {
        fetchUsers();
      }
    });
    return unsubscribe;
  }, []);

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setLoginError(error.message);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUsers([]);
      setSelectedUser(null);
      setConversations([]);
      setSelectedConversation(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Fetch all users (devices)
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const usersList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
    } catch (err) {
      setError('Failed to fetch users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch conversations for selected user
  const fetchConversations = async (userId) => {
    setLoading(true);
    setError(null);
    setConversations([]);
    setSelectedConversation(null);
    
    try {
      const conversationsRef = collection(db, 'users', userId, 'conversations');
      const q = query(conversationsRef, orderBy('created_at', 'desc'), limit(50));
      const conversationsSnapshot = await getDocs(q);
      
      const conversationsList = conversationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setConversations(conversationsList);
    } catch (err) {
      setError('Failed to fetch conversations: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    fetchConversations(user.id);
  };

  // Format transcript segments for display
  const formatTranscript = (segments) => {
    // Handle various data formats
    if (!segments) return <p>No transcript available</p>;
    
    let decompressedSegments = segments;
    
    // Try to decompress if it's compressed data
    if (typeof segments === 'object' && !Array.isArray(segments)) {
      // Check if it's a Firestore Bytes object and try to decompress
      if (segments._byteString || segments.bytes || segments._bytes) {
        console.log('Attempting to decompress Firestore Bytes data...');
        decompressedSegments = decompressTranscript(segments);
        
        if (!decompressedSegments) {
          return (
            <div>
              <p>Unable to decompress transcript data. It might be encrypted.</p>
              <details>
                <summary>Debug info</summary>
                <pre style={{fontSize: '10px'}}>
                  Data type: Firestore Bytes
                  {segments._byteString ? 'Has _byteString property' : ''}
                  {segments.bytes ? 'Has bytes property' : ''}
                </pre>
              </details>
            </div>
          );
        }
      }
    }
    
    // If it's a string, try to decompress
    if (typeof decompressedSegments === 'string') {
      console.log('Attempting to decompress string data...');
      decompressedSegments = decompressTranscript(decompressedSegments);
      
      if (!decompressedSegments) {
        return <p>Transcript data is encrypted and cannot be decrypted without the key.</p>;
      }
    }
    
    // Check if we have a valid array after decompression
    if (!Array.isArray(decompressedSegments)) {
      console.log('Unexpected format after decompression:', decompressedSegments);
      return <p>Transcript format not recognized after decompression</p>;
    }
    
    // If array is empty
    if (decompressedSegments.length === 0) return <p>No transcript available</p>;
    
    console.log('Successfully decompressed', decompressedSegments.length, 'segments');
    
    return decompressedSegments.map((segment, index) => {
      const speaker = segment.is_user ? 'User' : `Speaker ${segment.speaker_id || 0}`;
      const timestamp = segment.start ? new Date(segment.start * 1000).toISOString().substr(11, 8) : '00:00:00';
      return (
        <div key={index} className="transcript-segment">
          <span className="timestamp">[{timestamp}]</span>
          <span className={`speaker ${segment.is_user ? 'user' : 'other'}`}>{speaker}:</span>
          <span className="text">{segment.text || '(no text)'}</span>
        </div>
      );
    });
  };

  // Format structured data (AI analysis)
  const formatStructuredData = (structured) => {
    if (!structured) return null;
    
    return (
      <div className="structured-data">
        <h3>AI Analysis Results</h3>
        <div className="analysis-item">
          <strong>Title:</strong> {structured.title || 'N/A'}
        </div>
        <div className="analysis-item">
          <strong>Category:</strong> {structured.category || 'N/A'}
        </div>
        <div className="analysis-item">
          <strong>Overview:</strong> {structured.overview || 'N/A'}
        </div>
        {structured.action_items && structured.action_items.length > 0 && (
          <div className="analysis-item">
            <strong>Action Items:</strong>
            <ul>
              {structured.action_items.map((item, idx) => (
                <li key={idx}>
                  {item.description} {item.completed ? '✓' : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        {structured.events && structured.events.length > 0 && (
          <div className="analysis-item">
            <strong>Events:</strong>
            <ul>
              {structured.events.map((event, idx) => (
                <li key={idx}>
                  {event.title} - {
                    event.start?.seconds 
                      ? new Date(event.start.seconds * 1000).toLocaleString()
                      : event.start 
                        ? new Date(event.start).toLocaleString() 
                        : 'No date'
                  }
                  ({event.duration || 0} mins)
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Show login form if not authenticated
  if (!user) {
    return (
      <div className="App">
        <div className="login-container">
          <div className="login-box">
            <h1>OMI Admin Login</h1>
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="admin@example.com"
                />
              </div>
              <div className="form-group">
                <label>Password:</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter password"
                />
              </div>
              {loginError && <p className="error">{loginError}</p>}
              <button type="submit" className="login-button">Login</button>
            </form>
            <div className="login-note">
              <p>Note: Use your Firebase admin credentials to login</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>OMI Device Analysis Dashboard</h1>
        <div className="header-right">
          <span className="user-email">{user.email}</span>
          <button onClick={handleLogout} className="logout-button">Logout</button>
        </div>
      </header>
      
      <div className="container">
        <div className="sidebar">
          <h2>Devices/Users</h2>
          {loading && <p>Loading...</p>}
          {error && <p className="error">{error}</p>}
          <ul className="user-list">
            {users.map(user => (
              <li 
                key={user.id} 
                className={selectedUser?.id === user.id ? 'selected' : ''}
                onClick={() => handleUserSelect(user)}
              >
                <div className="user-item">
                  <span className="user-id">{user.id}</span>
                  {user.name && <span className="user-name">{user.name}</span>}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="main-content">
          {selectedUser ? (
            <>
              <h2>Conversations for {selectedUser.name || selectedUser.id}</h2>
              
              <div className="conversations-section">
                <div className="conversation-list">
                  <h3>Recent Conversations</h3>
                  <div className="filter-section">
                    <label className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={hideUntitled}
                        onChange={(e) => setHideUntitled(e.target.checked)}
                      />
                      <span>Hide untitled conversations</span>
                    </label>
                  </div>
                  {conversations.length === 0 ? (
                    <p>No conversations found</p>
                  ) : (
                    (() => {
                      const filteredConversations = conversations.filter(conv => {
                        if (hideUntitled) {
                          const title = conv.structured?.title;
                          return title && title.toLowerCase() !== 'untitled' && title.trim() !== '';
                        }
                        return true;
                      });
                      
                      return (
                        <>
                          <div className="conversation-count">
                            Showing {filteredConversations.length} of {conversations.length} conversations
                          </div>
                          <ul>
                            {filteredConversations.map(conv => (
                        <li 
                          key={conv.id}
                          className={selectedConversation?.id === conv.id ? 'selected' : ''}
                          onClick={() => setSelectedConversation(conv)}
                        >
                          <div className="conversation-item">
                            <span className="conv-title">
                              {conv.structured?.title || 'Untitled'}
                            </span>
                            <span className="conv-date">
                              {(() => {
                                if (conv.created_at?.seconds) {
                                  return new Date(conv.created_at.seconds * 1000).toLocaleString();
                                } else if (conv.created_at) {
                                  return new Date(conv.created_at).toLocaleString();
                                }
                                return 'No date';
                              })()}
                            </span>
                            <span className={`conv-status ${conv.status}`}>
                              {conv.status || 'completed'}
                            </span>
                          </div>
                        </li>
                            ))}
                          </ul>
                        </>
                      );
                    })()
                  )}
                </div>

                {selectedConversation && (
                  <div className="conversation-detail">
                    <h3>Conversation Details</h3>
                    
                    <div className="detail-section">
                      <h4>Metadata</h4>
                      <p><strong>ID:</strong> {selectedConversation.id}</p>
                      <p><strong>Status:</strong> {selectedConversation.status || 'completed'}</p>
                      <p><strong>Source:</strong> {selectedConversation.source || 'omi'}</p>
                      <p><strong>Language:</strong> {selectedConversation.language || 'en'}</p>
                      {selectedConversation.started_at && (
                        <p><strong>Started:</strong> {
                          selectedConversation.started_at.seconds 
                            ? new Date(selectedConversation.started_at.seconds * 1000).toLocaleString()
                            : new Date(selectedConversation.started_at).toLocaleString()
                        }</p>
                      )}
                      {selectedConversation.finished_at && (
                        <p><strong>Finished:</strong> {
                          selectedConversation.finished_at.seconds
                            ? new Date(selectedConversation.finished_at.seconds * 1000).toLocaleString()
                            : new Date(selectedConversation.finished_at).toLocaleString()
                        }</p>
                      )}
                    </div>

                    {formatStructuredData(selectedConversation.structured)}

                    <div className="detail-section">
                      <h4>Transcript</h4>
                      <div className="transcript-container">
                        {formatTranscript(selectedConversation.transcript_segments)}
                      </div>
                    </div>

                    {selectedConversation.apps_results && selectedConversation.apps_results.length > 0 && (
                      <div className="detail-section">
                        <h4>App Integration Results</h4>
                        {selectedConversation.apps_results.map((result, idx) => (
                          <div key={idx} className="app-result">
                            <strong>App {result.app_id || idx + 1}:</strong>
                            <p>{result.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="no-selection">
              <p>Select a device/user from the sidebar to view conversations</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;